import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService, LC_KEY_ACCOUNT_PRODUCT_SHORT, LC_KEY_ACCOUNT_PRODUCT_SHORT_HISTORY_LIST, LC_PRODUCT_SHORT_STORAGE_TIME, LocalStorage } from 'src/toolkit';
import { TTableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { TProductShortList, TProductShortListResponse, TAccountProductShortListQuery, adaptProductShortFromListResponse, TProductShortListSortCol, TAccountProductShortPayloadRequest, adaptAccountProductShortToRequest, TAccountProductShortHistoryItem } from '../../types';
import { BACKEND_ACCOUNT_PRODUCT_SHORT } from '../../backend-paths';

function saveToLocalStorage(payload: TAccountProductShortPayloadRequest) {
	const historyItemList: TAccountProductShortHistoryItem[] = LocalStorage.get(LC_KEY_ACCOUNT_PRODUCT_SHORT_HISTORY_LIST) || [];

	const date = new Date();
	const itemKey = JSON.stringify(payload);

	const historyItem: TAccountProductShortHistoryItem = {
		...payload,
		date: date.toISOString(),
		key: itemKey,
	};

	const filtered = historyItemList.filter(item => item.key !== itemKey);

	filtered.unshift(historyItem);

	if (filtered.length > 10) {
		filtered.length = 10;
	}

	LocalStorage.set(LC_KEY_ACCOUNT_PRODUCT_SHORT_HISTORY_LIST, filtered);
}

export const accountProductShortListGetApi = createAsyncThunk(
	BACKEND_ACCOUNT_PRODUCT_SHORT,
	(payload: TAccountProductShortPayloadRequest, thunkApi) => {
		const params = adaptAccountProductShortToRequest(payload);

		const key = `${LC_KEY_ACCOUNT_PRODUCT_SHORT}/${JSON.stringify(params)}`;
		const value = LocalStorage.get(key);

		if (value) {
			const { updatedTimestampAt, result } = value;
			const diff = Date.now() - updatedTimestampAt;

			if (diff <= LC_PRODUCT_SHORT_STORAGE_TIME) {
				console.log('FROM STORAGE');

				saveToLocalStorage(payload);

				return result;
			}
		}

		console.log('FROM DATABASE');

		return HttpService.get<TProductShortListResponse>(BACKEND_ACCOUNT_PRODUCT_SHORT, { params }).then(response => {
			const { data } = response;
			const result = adaptProductShortFromListResponse(data);

			// LocalStorage.set(key, { updatedTimestampAt: Date.now(), result });

			saveToLocalStorage(payload);

			return result;
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean;
	list: TProductShortList;
	showList: TProductShortList;
	query: TAccountProductShortListQuery;
	error?: HttpError;
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	showList: {
		items: [],
		count: 0,
	},
	query: {
		limit: 50,
		offset: 0,
		search: undefined,
		searchBrandName: undefined,
		searchSerialNumber: undefined,
		searchProvider: undefined,
		searchName: undefined,
		serialNumber: '',
	},
	error: undefined,
};

export const accountProductShortListSlice = createSlice({
	name: 'accountProductShortListSlice',
	initialState,
	reducers: {
		setAccountProductShortListQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setAccountProductShortListQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setAccountProductShortListQueryShortList(state, action: PayloadAction<TProductShortList>) {
			state.showList = action.payload;
		},

		setAccountProductShortListQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setAccountProductShortListQuerySearchBrandName(state, action: PayloadAction<string>) {
			const value = action.payload || undefined;
			const isValue = value && value.length > 0;

			if (isValue && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.searchBrandName = value;
		},

		setAccountProductShortListQuerySearchSerialNumber(state, action: PayloadAction<string>) {
			const value = action.payload || undefined;
			const isValue = value && value.length > 0;

			if (isValue && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.searchSerialNumber = value;
		},

		setAccountProductShortListQuerySearchProvider(state, action: PayloadAction<string>) {
			const value = action.payload || undefined;
			const isValue = value && value.length > 0;

			if (isValue && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.searchProvider = value;
		},

		setAccountProductShortListQuerySearchName(state, action: PayloadAction<string>) {
			const value = action.payload || undefined;
			const isValue = value && value.length > 0;

			if (isValue && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.searchName = value;
		},

		setAccountProductShortListQuerySerialNumber(state, action: PayloadAction<string>) {
			const serialNumber = action.payload || undefined;

			state.query.serialNumber = serialNumber ? serialNumber : '';
		},

		setAccountProductShortListQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setAccountProductShortListQuerySortCol(state, action: PayloadAction<TProductShortListSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearAccountProductShortList(state) {
			return { ...initialState, list: state.list, query: state.query };
		},

		clearAllAccountProductShortList(state) {
			const query = {
				...state.query,
				serialNumber: '',
				search: '',
				offset: 0,
			};

			return { ...initialState, query };
		},
	},

	extraReducers: {
		[accountProductShortListGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[accountProductShortListGetApi.fulfilled.type]: (state, action: PayloadAction<TProductShortList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[accountProductShortListGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useAccountProductShortListSelector = () => useAppSelector(state => state[accountProductShortListSlice.name]);

export const { clearAccountProductShortList, clearAllAccountProductShortList, setAccountProductShortListQuerySearch, setAccountProductShortListQuerySearchBrandName, setAccountProductShortListQuerySearchSerialNumber, setAccountProductShortListQuerySearchProvider, setAccountProductShortListQuerySearchName, setAccountProductShortListQuerySerialNumber, setAccountProductShortListQuerySort, setAccountProductShortListQuerySortCol, setAccountProductShortListQueryLimit, setAccountProductShortListQueryOffset, setAccountProductShortListQueryShortList } = accountProductShortListSlice.actions;
